import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { FibLevel, LineStyleName, Timeframe } from "@/api/types";
import type { AppTheme } from "./appearanceSlice";
import { DEFAULT_TAB_ID, addTab, removeTab } from "./tabsSlice";
import { hydrateWorkspace } from "./workspacesSlice";
import { defaultAnalysisRange } from "@/utils/chartTime";

export type ChartType = "line" | "candle" | "area";

// Last-used trade volume, persisted per-device (like the app theme) so it's
// remembered across reloads — set the lot once and every new order (one-click
// or pending draft) seeds from it. App.tsx mirrors changes back to storage.
export const TRADE_VOLUME_STORAGE_KEY = "tv-trade-volume";

// Coach marks used to live here as four independent, self-dismissing hints.
// They are gone: see components/Tour — one sequenced, click-advanced tour,
// persisted per USER on the server, so it plays exactly once and waits for the
// reader instead of vanishing on a 6-second timer.

function readInitialTradeVolume(): number {
  try {
    const raw = Number(localStorage.getItem(TRADE_VOLUME_STORAGE_KEY));
    if (isFinite(raw) && raw > 0) return Math.max(0.01, Math.round(raw * 100) / 100);
  } catch {
    /* storage blocked — fall through to default */
  }
  return 0.01;
}

export interface MaLine {
  id: string;
  enabled: boolean;
  period: number;
  type: "SMA" | "EMA" | "Anchored";
  color: string;
  /** UTC anchor the Anchored MA calculates FROM. Two shapes (see indicators.ts):
   *  date-only "YYYY-MM-DD" starts at that trading day's session open; hour-precise
   *  "YYYY-MM-DDTHH:mm" starts at that exact UTC hour. The Anchored type ignores
   *  `period` (it averages every bar since the anchor). Ignored for SMA/EMA.
   *  Optional — defaults to the start of the previous week. */
  anchorDate?: string;
  /** UTC end the Anchored MA calculates UP TO (inclusive). Date-only "YYYY-MM-DD"
   *  includes the whole trading day; hour-precise "YYYY-MM-DDTHH:mm" stops at that
   *  exact UTC hour. Bounds the window to [anchorDate, endDate] so a fixed
   *  historical span can be pinned for backtesting. Ignored for SMA/EMA. Optional
   *  — when unset the line runs from the anchor to the present. */
  endDate?: string;
}

export interface RsiSettings {
  enabled: boolean;
  period: number;
  overbought: number;
  oversold: number;
  color: string;
}

/** Anchor-Time RSI — a SEPARATE indicator from the period RSI. Cumulative RSI
 *  over every bar since `anchorDate` (no period). Renders in the same RSI pane
 *  and shares the period RSI's overbought/oversold reference lines. */
export interface AnchorRsiSettings {
  enabled: boolean;
  /** UTC anchor (see MaLine.anchorDate): date-only "YYYY-MM-DD" starts at the
   *  trading day's session open, hour-precise "YYYY-MM-DDTHH:mm" at that exact UTC
   *  hour. Defaults to the start of the previous week. */
  anchorDate?: string;
  /** UTC end (see MaLine.endDate): date-only includes the whole trading day,
   *  hour-precise "YYYY-MM-DDTHH:mm" stops at that exact UTC hour. Bounds the
   *  window to [anchorDate, endDate] for backtesting. Optional — when unset the
   *  line runs from the anchor to the present. */
  endDate?: string;
  color: string;
}

export interface IndicatorSettings {
  maLines: MaLine[];
  rsi: RsiSettings;
  anchorRsi: AnchorRsiSettings;
}

export const DEFAULT_MA_LINE: Omit<MaLine, "id"> = {
  enabled: true,
  period: 20,
  type: "SMA",
  color: "#f59e0b",
};

export const DEFAULT_INDICATORS: IndicatorSettings = {
  maLines: [{ id: "ma-1", enabled: false, period: 20, type: "SMA", color: "#f59e0b" }],
  rsi: {
    enabled: false,
    period: 14,
    overbought: 70,
    oversold: 30,
    color: "#a78bfa",
  },
  anchorRsi: {
    enabled: false,
    color: "#38bdf8",
  },
};
export type DrawingTool =
  | "vertical"
  | "horizontal"
  | "diagonal"
  | "path"
  | "fibonacci"
  | "long"
  | "short"
  | "priceRange"
  | "text"
  | null;
export type DrawingKind =
  | "fib"
  | "hline"
  | "vline"
  | "diagonal"
  | "path"
  | "position"
  | "priceRange"
  | "text";
export interface SelectedDrawing {
  kind: DrawingKind;
  id: string;
}

export interface FibRetracement {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
  /** Per-drawing level visibility override. Ratios listed here are
   *  HIDDEN for this fib only, even if globally enabled. Subtractive
   *  + optional: absent/empty = show every globally-enabled level
   *  (the default, and how all existing fibs behave). Lets one fib
   *  show the full ladder while another shows a sparse subset. */
  hiddenLevels?: number[];
  /** Hide the diagonal trend line connecting the two anchors for this
   *  fib only (the level lines + handles stay). Optional; absent/false
   *  = shown (default). */
  hideDiagonal?: boolean;
  /** Which side of the fib the level labels (e.g. "61.8% (4294)") sit
   *  on. Defaults to "right". Useful for a fib nested inside another,
   *  where right-side labels would overlap the outer one. */
  labelSide?: "left" | "right";
  visible?: boolean;
  locked?: boolean;
}

export interface HorizontalLine {
  id: string;
  price: number;
  visible?: boolean;
  locked?: boolean;
  color?: string;
  width?: number;
  style?: LineStyleName;
  /** Optional free-form text drawn next to the line. */
  text?: string;
  /** Per-line text colour. Falls back to the line's stroke colour. */
  textColor?: string;
  /** Per-line label placement. Default "left" / "above". Edited from the
   *  contextual drawing toolbar. */
  textHPos?: "left" | "right";
  textVPos?: "above" | "below";
}

export interface VerticalLine {
  id: string;
  time: number;
  visible?: boolean;
  locked?: boolean;
  color?: string;
  width?: number;
  style?: LineStyleName;
}

/** Diagonal trend line — same anchors as a fib (two endpoints in
 *  time/price space) but rendered as a single connecting segment
 *  with no retracement levels. */
export interface DiagonalLine {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
  visible?: boolean;
  locked?: boolean;
  color?: string;
  width?: number;
  style?: LineStyleName;
}

export interface PathPoint {
  time: number;
  price: number;
}

/** N-vertex polyline. Always has ≥ 2 points (a 1-point draft is
 *  rejected at commit time). */
export interface Path {
  id: string;
  points: PathPoint[];
  visible?: boolean;
  locked?: boolean;
  color?: string;
  width?: number;
  style?: LineStyleName;
}

/** Freeform text annotation anchored to a chart coordinate. */
export interface TextLabel {
  id: string;
  time: number;
  price: number;
  text: string;
  visible?: boolean;
  locked?: boolean;
  color?: string;
  fontSize?: number;
  bold?: boolean;
}

export type PositionType = "long" | "short";

export interface Position {
  id: string;
  type: PositionType;
  /** Three price levels; TP > entry > SL for long, inverted for short.
   *  Stored as absolute prices so rendering doesn't need to know which
   *  side is "above" — it derives that from `type`. */
  entryPrice: number;
  slPrice: number;
  tpPrice: number;
  /** Time-range for the box. Right edge is draggable to extend
   *  forecast horizon. */
  startTime: number;
  endTime: number;
  visible?: boolean;
  locked?: boolean;
}

/** Price-range measurement box (TradingView "Price Range") — two anchors
 *  (A = start, B = end) defining a rectangle; the renderer derives price
 *  change / pips / % / bars between them. Anchor shape mirrors a fib /
 *  diagonal (start/end time+price) so it reuses the same drag flow.
 *  NB: distinct from `UiTabState.priceRange`, which is the visible
 *  price-AXIS range, not a drawing. */
export interface PriceRangeBox {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
  visible?: boolean;
  locked?: boolean;
  /** Draggable midline level as a fraction of the range (startPrice→endPrice).
   *  0.5 = the 50% pullback (default). Per-box so each can sit at its own % . */
  midlineFraction?: number;
  /** Horizontal position of the centre arrow as a fraction of the time span
   *  (startTime→endTime). 0.5 = centred (default). Per-box so the arrow can be
   *  slid left/right out of the way of important candles. */
  arrowFraction?: number;
}

/** Per-tab UI state. Everything that conceptually belongs to "the
 *  chart you're looking at" lives here. Modals shared across tabs
 *  (fib levels editor, position settings) stay at the slice root. */
export interface UiTabState {
  symbol: string;
  timeframe: Timeframe;
  analysisStart: string;
  analysisEnd: string;
  chartType: ChartType;
  activeDrawingTool: DrawingTool;
  fibAnchor: { time: number; price: number } | null;
  fibRetracements: FibRetracement[];
  horizontalLines: HorizontalLine[];
  verticalLines: VerticalLine[];
  /** First click of a two-click diagonal-line placement. */
  diagonalAnchor: { time: number; price: number } | null;
  diagonalLines: DiagonalLine[];
  /** In-progress path placement — accumulates vertices on each click. */
  pathDraft: PathPoint[] | null;
  paths: Path[];
  positions: Position[];
  /** First click of a two-click position placement (entry anchor). */
  positionAnchor: { time: number; price: number } | null;
  /** Price-range measurement boxes (TradingView "Price Range"). */
  priceRangeBoxes: PriceRangeBox[];
  /** First click of a two-click price-range placement. */
  priceRangeAnchor: { time: number; price: number } | null;
  /** Freeform text annotations anchored to chart coordinates. */
  textLabels: TextLabel[];
  /** Pending text-label placement anchor (set on first click while
   *  the text tool is active; cleared when the label commits). */
  textAnchor: { time: number; price: number } | null;
  /** Drawing currently picked by the user (for keyboard delete, etc). */
  selectedDrawing: SelectedDrawing | null;
  /** Last-known visible time range, in UTC seconds. Updated by the
   *  Chart on pan/zoom (debounced) so the workspace auto-save can
   *  capture it. Restored on workspace hydrate / tab switch so each
   *  chart re-opens at the user's last pan position. Null until the
   *  chart has been viewed at least once. */
  viewport: { from: number; to: number } | null;
  /** Right price-scale margins (top, bottom, 0..0.45 each). The chart
   *  is a single mounted instance shared across tabs, so the user's
   *  ctrl+wheel-driven scaleMargins would otherwise bleed across tabs.
   *  Captured/applied on tab switch alongside `viewport`. Null = use
   *  lightweight-charts' default (0.1, 0.1). */
  scaleMargins: { top: number; bottom: number } | null;
  /** Visible price range from the user's right-axis drag. `from` is
   *  the price at the chart's bottom edge, `to` is at the top.
   *  Captured via series.coordinateToPrice() at switch-away; restored
   *  via the series's autoscaleInfoProvider on switch-back. Null =
   *  let autoScale fit the visible candles (the chart's default).
   *  Without this, dragging the right axis on one tab gets erased
   *  the moment the user switches away because the autoScale toggle
   *  we apply on tab change re-fits the price scale. */
  priceRange: { from: number; to: number } | null;
  /** Show/hide the trading server entry lines (both open positions and
   *  pending orders) on this chart. Defaults ON — your own open trades
   *  should be visible on your own chart without hunting through a menu.
   *  Per-tab, so a clean analysis tab can still turn them off. Polling runs
   *  regardless; the toggle only suppresses rendering on this one chart. */
  showTradingTrades: boolean;
  /** Show/hide the SL/TP lines that go with each the trading server trade.
   *  Independent from `showTradingTrades` so the user can show just
   *  the entry/trigger price without all the SL/TP clutter when
   *  reviewing a lot of trades. */
  showTradingSlTp: boolean;
  /** Show/hide closed-trade markers on the chart. When on, every
   *  matching closed trade renders as an entry arrow + an exit
   *  arrow on the candle at its open/close time (bucketed to this
   *  tab's timeframe), with exit colored by realised P&L. Per-tab
   *  because the marker density can clutter a busy chart — the
   *  user can keep them off on a live trading tab and on for
   *  review/journal tabs. */
  showHistoryOrders: boolean;
  /** Per-tab theme override. `null` (or absent) = inherit from
   *  User scope (or built-in defaults if the user hasn't saved
   *  any user-scope theme). When set, this tab's chart uses
   *  these colors. Edited via the Chart Properties dialog's
   *  "Workspace" scope (which in practice scopes to the active
   *  tab — the simplified 2-scope model dropped the global
   *  workspace-wide level). */
  theme?: ChartTheme | null;
  /** Per-tab per-scheme cache so flipping presets in the dialog
   *  at this tab preserves each scheme's customisations
   *  (same pattern User scope uses). */
  themeCache?: Partial<Record<ChartScheme, ChartTheme>>;
  /** LIGHT-mode counterparts of theme/themeCache. `theme`/
   *  `themeCache` above are the DARK-mode slot (and what every
   *  legacy save carries); these hold the user's separate
   *  customisation for light mode. Absent = light inherits the
   *  preset (whose default bg auto-whitens). The active slot is
   *  picked by selectEffectiveTheme from the current app theme. */
  themeLight?: ChartTheme | null;
  themeCacheLight?: Partial<Record<ChartScheme, ChartTheme>>;
  /** Per-tab Fibonacci levels override. Same inheritance
   *  semantics as `theme`. */
  fibLevels?: FibLevel[] | null;
  /** Per-tab fib stroke width override (1..4 px). Shared across
   *  every level + the trend line. Inherits from User-scope if
   *  null/undefined. */
  fibLineWidth?: number | null;
  /** Per-tab fib stroke style override. Same inheritance rules. */
  fibLineStyle?: LineStyleName | null;
  /** Per-tab Weekly Behaviour settings — day-of-week + week-by-
   *  week session band highlighting. null/undefined = inherit
   *  from User scope (or built-in DEFAULT_WEEKLY_BEHAVIOUR). */
  weeklyBehaviour?: WeeklyBehaviourSettings | null;
  /** Per-tab indicator settings (MA + RSI). null/absent = disabled
   *  (no indicators). Persisted in workspace JSON. */
  indicators?: IndicatorSettings | null;
}

// ---- Weekly Behaviour types ----
//
// Two band-rendering modes:
//   - Mode A (dayHighlights): one band per session-day, colour
//     determined by the day-of-week index.
//   - Mode B (weekMode): one band per session-week, colour
//     cycles through `palette` by week index.
//
// Both modes are independent; either, both, or neither can be
// enabled per scope. "Session day" = forex trading session
// (Sunday ~22:00 UTC opens Monday session; Friday ~22:00 UTC
// closes Friday session); calendar midnight is not used.

export interface DayHighlight {
  /** 0 = Monday … 4 = Friday. Forex sessions only span Mon–Fri. */
  dayOfWeek: number;
  /** #RRGGBB or rgba(...). Same encoding the chart theme uses
   *  (round-trips through colorWithAlpha.parseColor). */
  color: string;
  enabled: boolean;
}

export interface WeekModeSettings {
  enabled: boolean;
  /** Rotating palette for week-by-week bands. Each week index i
   *  picks palette[i % palette.length]. Defaults to 6 hues that
   *  read well on the dark chart. */
  palette: string[];
}

/** Which mode(s) actually render bands. The Day/Week tabs in the
 *  dialog let the user edit each mode's settings independently;
 *  this selector controls which of those edits is currently
 *  visible on the chart. */
export type WeeklyBehaviourMode = "day" | "week" | "both";

export type ChatPanelTab = "public" | "support";

export interface WeeklyBehaviourSettings {
  dayHighlights: DayHighlight[];  // length 5 (Mon..Fri)
  weekMode: WeekModeSettings;
  /** Which mode renders. Defaults to "both" so a fresh-customize
   *  surfaces whatever the user enables in either tab. */
  mode: WeeklyBehaviourMode;
  /** YYYY-MM-DD; null = open-ended on that side. */
  fromDate: string | null;
  toDate: string | null;
}

// All defaults bake in a ~0.18 alpha so bands render translucent
// out of the box and candles stay readable behind them. The
// ColorPicker round-trips rgba() cleanly via parseColor /
// formatColor, so the user can tune alpha (and hue) per-row from
// the dialog without losing either.
export const DEFAULT_WEEK_PALETTE: string[] = [
  "rgba(79, 195, 247, 0.18)",  // cyan
  "rgba(186, 104, 200, 0.18)", // purple
  "rgba(255, 183, 77, 0.18)",  // orange
  "rgba(129, 199, 132, 0.18)", // green
  "rgba(100, 181, 246, 0.18)", // blue
  "rgba(240, 98, 146, 0.18)",  // pink
];

export const DEFAULT_WEEKLY_BEHAVIOUR: WeeklyBehaviourSettings = {
  dayHighlights: [
    { dayOfWeek: 0, color: "rgba(79, 195, 247, 0.18)",  enabled: false }, // Mon
    { dayOfWeek: 1, color: "rgba(186, 104, 200, 0.18)", enabled: false }, // Tue
    { dayOfWeek: 2, color: "rgba(255, 183, 77, 0.18)",  enabled: false }, // Wed
    { dayOfWeek: 3, color: "rgba(129, 199, 132, 0.18)", enabled: false }, // Thu
    { dayOfWeek: 4, color: "rgba(240, 98, 146, 0.18)",  enabled: false }, // Fri
  ],
  weekMode: {
    enabled: false,
    palette: [...DEFAULT_WEEK_PALETTE],
  },
  mode: "both",
  fromDate: null,
  toDate: null,
};

interface UiState {
  byTab: Record<string, UiTabState>;
  /** Toolbar / chart context menu both flip this on to open the
   *  shared Fibonacci-levels editor dialog. Global because the dialog
   *  lives in the toolbar, not a specific tab's chart. */
  fibLevelsDialogOpen: boolean;
  /** Position id currently being edited via the PositionDialog
   *  (right-click "Edit settings"). The dialog is shared across tabs;
   *  the id is sufficient to find the position in whichever tab owns
   *  it. null when dialog is closed. */
  positionDialogId: string | null;
  /** Whether the trade-journal drawer is open. App.tsx mounts the
   *  drawer when true; the journal icon in RightSidebar toggles
   *  this. Global (not per-tab) — the journal aggregates trades
   *  across every tab + every symbol. */
  journalOpen: boolean;
  /** Whether the "Chart colors…" theme-editor dialog is open. */
  chartColorsDialogOpen: boolean;
  /** Tool whose settings panel is open (registry tool id), or null. Global so
   *  the left Toolbar, the per-drawing toolbar, AND a drawing's right-click
   *  menu all open the same single panel. Not persisted (transient UI). */
  settingsToolId: string | null;
  /** Whether the "Weekly Behaviour" dialog is open. */
  weeklyBehaviourDialogOpen: boolean;
  /** Whether the freeform Notes drawer is open. */
  notesOpen: boolean;
  /** Whether the Image Board (every screenshot from notes + journal
   *  in one grid) is open. A fullscreen overlay, not a docked
   *  drawer, so it isn't part of the mutual-exclusion group — it
   *  sits above whichever drawer launched it. */
  imageBoardOpen: boolean;
  /** One-shot "open this note and expand it" request, or null.
   *
   *  The board's "Jump to note" needs to reach into a drawer that
   *  keeps its expanded set in local component state. Rather than
   *  hoist all of that, the board parks an id here; the drawer
   *  consumes it (expand + scroll into view) and clears it. Null
   *  between jumps — it is a request, not a selection. */
  focusNoteId: string | null;
  /** Same one-shot contract as focusNoteId, for a journal entry
   *  key (`pos:<id>` / `pending:<ticket>`). */
  focusJournalKey: string | null;
  /** Whether the signal chart pane (shared read-only bot_all reference) is
   *  open. A pane, not a docked drawer, so it's independent of
   *  notes/journal/help mutual exclusivity. */
  signalChartOpen: boolean;
  /** Whether the Signal orders docked drawer (bot_all running positions +
   *  pending orders) is open. Toggled from the right-rail signal button, and
   *  opened automatically alongside the Signal chart tab. */
  signalOrdersOpen: boolean;
  /** Whether the Help & Contact docked panel is open. Shares the
   *  docked-drawer slot with Notes / Journal / News, so they are kept
   *  mutually exclusive. */
  helpOpen: boolean;
  /** Whether the economic-news calendar drawer is open. Same docked slot
   *  as Notes / Journal / Help. Toggled by the Calendar icon in the right
   *  rail; available to every signed-in user. */
  newsOpen: boolean;
  /** Whether the chat panel (Public room / Support) is open. Same docked slot
   *  as Notes / Journal / Help / News. Toggled by the chat icon in the right
   *  rail. */
  chatOpen: boolean;
  /** The chat panel's active tab. Kept here, not in the panel, so it survives
   *  closing and reopening. */
  chatTab: ChatPanelTab;
  /** Whether the admin web terminal modal is open. Admin-only, full-screen
   *  overlay (not part of the docked-drawer slot), so it's a standalone
   *  boolean. */
  terminalOpen: boolean;
  /** Whether the Objects Tree (layer manager) panel is open. */
  objectsTreeOpen: boolean;
  /** Whether the trading-style one-click trading panel is shown on the
   *  chart. Global (master-only) — toggled from the chart context menu
   *  or the `O` hotkey; the active chart renders the docked panel. */
  oneClickPanelOpen: boolean;
  /** Whether the Trading panel modal (positions / orders / history) is
   *  open. Global (master-only) — opened from the header chip or the
   *  chart context menu's Trading submenu. */
  tradingPanelOpen: boolean;
  /** Default order volume (lots) shared by the one-click panel and the
   *  right-click instant/pending trade items. */
  tradeVolume: number;
  /** Workspace-wide visual preferences. Persisted in the workspace
   *  payload so different workspaces can use different visuals. */
  prefs: {
    /** Legacy quick-toggle — kept so old workspaces hydrate
     *  cleanly. New theme system reads `theme.bg` directly; this
     *  flag is no longer consulted at render time except as the
     *  source for first-hydrate theme derivation. */
    plainDarkChart: boolean;
    /** Legacy quick-toggle — same migration story as
     *  plainDarkChart. The new theme replaces it. */
    monoCandles: boolean;
    /** Active theme — what the chart actually renders right now.
     *  Equal to `themeCache[theme.scheme]` when both are present;
     *  exposed as a top-level field so existing chart code can
     *  read `prefs.theme.bullBody` without dereferencing the
     *  scheme key. */
    theme: ChartTheme;
    /** Per-scheme remembered customisations. Populated by Save in
     *  the Properties dialog so the user can flip between Red/Green
     *  and Mono Green/Black without losing their per-scheme tweaks.
     *  Missing key = use that scheme's built-in defaults. */
    themeCache: Partial<Record<ChartScheme, ChartTheme>>;
    /** Workspace-scope Fibonacci levels override. `null`/absent =
     *  inherit from User scope (the global fibLevelsSlice.levels,
     *  loaded from /api/fib_levels). Edited via the Fib dialog
     *  when its scope selector is on "Workspace". */
    fibLevels?: FibLevel[] | null;
  };
}

/** Names for the two built-in presets. The dialog's preset buttons
 *  rewrite the whole theme to one of these; user can then edit
 *  individual colors. `scheme` is stored too so the dialog can
 *  highlight the preset the theme started from. */
export type ChartScheme = "redGreen" | "monoBlack";

export interface ChartTheme {
  scheme: ChartScheme;
  /** Chart background. */
  bg: string;
  /** Whether to render the faint background grid. */
  showGrid: boolean;
  /** Bullish candle body fill. */
  bullBody: string;
  /** Bearish candle body fill. */
  bearBody: string;
  /** Bullish candle border + wick (shared). */
  bullBorderWick: string;
  /** Bearish candle border + wick (shared). */
  bearBorderWick: string;
  /** Pending-order trigger line. Like the live-price line, has
   *  its own color/width/style triplet so the LineStyleDialog
   *  can drive all three. */
  pendingOrder: string;
  pendingOrderWidth: number;
  pendingOrderStyle: LineStyleName;
  /** Stop-loss line (applies to open positions AND pending-order
   *  SL — single source of truth). Color/width/style. */
  slColor: string;
  slWidth: number;
  slStyle: LineStyleName;
  /** Take-profit line (open + pending). */
  tpColor: string;
  tpWidth: number;
  tpStyle: LineStyleName;
  /** Running-position ENTRY line, split by direction so a long and a
   *  short can read differently. Previously the entry line reused the
   *  candle body color, which is invisible under the Mono Black/Green
   *  preset (bull body = black). Now it's its own editable triplet.
   *  Optional in older saves — renderers + the editor fall back to the
   *  defaults below when a field is missing. */
  posLongColor?: string;
  posLongWidth?: number;
  posLongStyle?: LineStyleName;
  posShortColor?: string;
  posShortWidth?: number;
  posShortStyle?: LineStyleName;
  /** Live-price line — the horizontal line drawn at the latest
   *  close. Same triplet shape as the others. */
  priceLineColor: string;
  priceLineWidth: number;
  priceLineStyle: LineStyleName;
  /** Drawing-tool default styles. Each manual drawing tool
   *  (vertical / horizontal / trend / arrow) shares the same
   *  color+width+style triplet shape as the lines above and is
   *  edited from the same LineStyleDialog (right-click the tool in
   *  the toolbar). Applies to every drawing of that type on the
   *  chart. Optional in older saves — renderers fall back to the
   *  preset defaults when a field is missing. */
  vlineColor: string;
  vlineWidth: number;
  vlineStyle: LineStyleName;
  hlineColor: string;
  hlineWidth: number;
  hlineStyle: LineStyleName;
  /** Horizontal-line text-label defaults (type-level, edited in the
   *  settings modal). Per-line overrides live on `HorizontalLine`
   *  (`textColor` / `textHPos` / `textVPos`) and take precedence. */
  hlineTextColor?: string;
  hlineTextHPos?: "left" | "right";
  hlineTextVPos?: "above" | "below";
  trendColor: string;
  trendWidth: number;
  trendStyle: LineStyleName;
  arrowColor: string;
  arrowWidth: number;
  arrowStyle: LineStyleName;
  /** Price Range measurement tool styling (per-user, per-mode). A single
   *  configurable colour (absent → default light green), not direction-based.
   *  `priceRangeTextMatch` (default true) makes the label follow the box
   *  colour; when false it uses `priceRangeTextColor`. Edited from the toolbar
   *  right-click → Edit settings. */
  priceRangeColor?: string;
  priceRangeTextColor?: string;
  priceRangeTextMatch?: boolean;
  /** Which side of the box the two edge price values render on (default
   *  "right"). */
  priceRangeLabelSide?: "left" | "right";
  /** Box border line thickness in px (default 2). */
  priceRangeBoxWidth?: number;
  /** Label font weight: "normal" (400) or "bold" (700, default). */
  priceRangeLabelWeight?: "normal" | "bold";
  /** Centre vertical double-arrow — show/hide (default on). Position is
   *  per-box (`PriceRangeBox.arrowFraction`); this is just the global toggle. */
  priceRangeArrow?: boolean;
  /** 50% midline (the wave's pullback level) — show/hide (default on),
   *  line style + thickness, drawn at the box's vertical centre with a
   *  "50% · <mid price>" label. */
  priceRangeMidline?: boolean;
  priceRangeMidlineStyle?: LineStyleName;
  priceRangeMidlineWidth?: number;
  /** Text-annotation tool defaults (per-tab, per-mode). Applied to text
   *  labels that have no per-instance override. Edited from the text
   *  drawing's settings gear. */
  textLabelColor?: string;
  textLabelFontSize?: number;
  textLabelBold?: boolean;
}

/** Stroke style — single canonical definition lives in `@/api/types`
 *  (`LineStyleName`, imported above); re-exported here so the many
 *  existing `import { LineStyleName } from "uiSlice"` sites keep
 *  working. */
export type { LineStyleName };

/** The two built-in presets. The dialog's preset buttons replace
 *  the whole theme with one of these; per-color edits afterwards
 *  diverge from the preset (the scheme tag still records which
 *  preset the user started from). */
export const CHART_SCHEMES: Record<ChartScheme, ChartTheme> = {
  redGreen: {
    scheme: "redGreen",
    /* Default chart background — rgb(12,17,28) (--surface), so the chart
       sits a touch above the darker rail/chrome frame. */
    bg: "#0c111c",
    // Default to no grid — cleaner chart out of the box; the user can
    // re-enable it from Chart Properties.
    showGrid: false,
    bullBody: "#26a69a",
    bearBody: "#ef5350",
    bullBorderWick: "#26a69a",
    bearBorderWick: "#ef5350",
    pendingOrder: "#22d3ee",
    pendingOrderWidth: 1,
    // LargeDashed matches the crosshair so a pending trigger
    // reads as "watching this price" by default.
    pendingOrderStyle: "largeDashed",
    slColor: "#ef5350",
    slWidth: 1,
    slStyle: "dashed",
    tpColor: "#26a69a",
    tpWidth: 1,
    tpStyle: "dashed",
    // Running-position entry lines — visible on both light + dark/mono
    // backgrounds (the old body-color reuse went invisible on Mono Black).
    posLongColor: "#26a69a",
    posLongWidth: 1,
    posLongStyle: "dotted",
    posShortColor: "#ef5350",
    posShortWidth: 1,
    posShortStyle: "dotted",
    priceLineColor: "#787b86",
    priceLineWidth: 1,
    priceLineStyle: "solid",
    // Drawing-tool defaults — match the original hard-coded look.
    vlineColor: "#FF9800",
    vlineWidth: 1,
    vlineStyle: "dashed",
    hlineColor: "#2962FF",
    hlineWidth: 1,
    hlineStyle: "dashed",
    hlineTextColor: "#2962FF",
    hlineTextHPos: "left",
    hlineTextVPos: "above",
    trendColor: "#2962FF",
    trendWidth: 1,
    trendStyle: "solid",
    arrowColor: "#2962FF",
    arrowWidth: 3,
    arrowStyle: "solid",
  },
  monoBlack: {
    scheme: "monoBlack",
    // Match the --surface chart bg used by Red/Green so the chart sits a
    // touch above the darker rail/chrome frame (was pure #000000).
    bg: "#0c111c",
    showGrid: false,
    // Near-black bullish bodies read as empty (border-only) against the
    // dark-blue bg — same dark-theme convention we've been using.
    bullBody: "#000000",
    bearBody: "#ffffff",
    bullBorderWick: "#28a428",
    bearBorderWick: "#388e3c",
    pendingOrder: "#22d3ee",
    pendingOrderWidth: 1,
    pendingOrderStyle: "largeDashed",
    slColor: "#ef5350",
    slWidth: 1,
    slStyle: "dashed",
    tpColor: "#26a69a",
    tpWidth: 1,
    tpStyle: "dashed",
    // Running-position entry lines — visible on both light + dark/mono
    // backgrounds (the old body-color reuse went invisible on Mono Black).
    posLongColor: "#26a69a",
    posLongWidth: 1,
    posLongStyle: "dotted",
    posShortColor: "#ef5350",
    posShortWidth: 1,
    posShortStyle: "dotted",
    priceLineColor: "#787b86",
    priceLineWidth: 1,
    priceLineStyle: "solid",
    // Drawing tools aren't scheme-specific — same defaults as Red/Green.
    vlineColor: "#FF9800",
    vlineWidth: 1,
    vlineStyle: "dashed",
    hlineColor: "#2962FF",
    hlineWidth: 1,
    hlineStyle: "dashed",
    hlineTextColor: "#2962FF",
    hlineTextHPos: "left",
    hlineTextVPos: "above",
    trendColor: "#2962FF",
    trendWidth: 1,
    trendStyle: "solid",
    arrowColor: "#2962FF",
    arrowWidth: 3,
    arrowStyle: "solid",
  },
};

export function createDefaultUiTabState(): UiTabState {
  // Today 00:00 → 23:59 in UTC (chartTime is the single time source). With
  // today's end-time in the future, Run Analysis defaults to combined-mode.
  const { start, end } = defaultAnalysisRange();
  return {
    symbol: "XAUUSD",
    timeframe: "1H",
    analysisStart: start,
    analysisEnd: end,
    chartType: "candle",
    activeDrawingTool: null,
    fibAnchor: null,
    fibRetracements: [],
    horizontalLines: [],
    verticalLines: [],
    diagonalAnchor: null,
    diagonalLines: [],
    pathDraft: null,
    paths: [],
    positions: [],
    positionAnchor: null,
    priceRangeBoxes: [],
    priceRangeAnchor: null,
    textLabels: [],
    textAnchor: null,
    selectedDrawing: null,
    viewport: null,
    scaleMargins: null,
    priceRange: null,
    // The trading server overlay layers.
    //
    // Entry lines AND their SL/TP default ON. Defaulting them off made a
    // trader's own open positions invisible on their own chart until they
    // found a right-click menu item, and a position you cannot see is a
    // position you cannot manage — worse, one with no visible stop is
    // indistinguishable from one with no stop at all. Both layers are cheap:
    // they render only what the broker already reports for this symbol, and
    // a user who wants a clean analysis chart can still turn them off
    // per-tab.
    //
    // Closed-trade markers stay OFF: their density genuinely clutters a busy
    // chart, and they are review material rather than something you act on.
    showTradingTrades: true,
    showTradingSlTp: true,
    showHistoryOrders: false,
    // theme + fibLevels intentionally absent — fresh tabs inherit
    // from workspace scope (or User in Phase B).
  };
}

const initialState: UiState = {
  byTab: { [DEFAULT_TAB_ID]: createDefaultUiTabState() },
  fibLevelsDialogOpen: false,
  positionDialogId: null,
  journalOpen: false,
  chartColorsDialogOpen: false,
  settingsToolId: null,
  weeklyBehaviourDialogOpen: false,
  notesOpen: false,
  imageBoardOpen: false,
  focusNoteId: null,
  focusJournalKey: null,
  signalChartOpen: false,
  signalOrdersOpen: false,
  helpOpen: false,
  newsOpen: false,
  chatOpen: false,
  chatTab: "public",
  terminalOpen: false,
  objectsTreeOpen: false,
  oneClickPanelOpen: false,
  tradingPanelOpen: false,
  tradeVolume: readInitialTradeVolume(),
  prefs: {
    plainDarkChart: false,
    monoCandles: false,
    theme: CHART_SCHEMES.redGreen,
    themeCache: {},
    fibLevels: null,
  },
};

/** Lazy-initialise a tab's per-tab UI state on first write. Lets the
 *  tabs slice create a tab without pre-populating every per-tab slice
 *  in lockstep. */
function getTab(state: UiState, tabId: string): UiTabState {
  let t = state.byTab[tabId];
  if (!t) {
    t = createDefaultUiTabState();
    state.byTab[tabId] = t;
  }
  return t;
}

type WithTab<T> = { tabId: string } & T;

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setTimeframe(state, action: PayloadAction<WithTab<{ timeframe: Timeframe }>>) {
      const t = getTab(state, action.payload.tabId);
      t.timeframe = action.payload.timeframe;
      // Drop the saved viewport — its absolute UTC time range maps to
      // a wildly different number of bars on a different TF (e.g. a
      // 10-month 1D viewport = ~300 bars but the same range on 1H is
      // ~7000 bars, squashed to 1 px each). Forcing viewport to null
      // makes the fresh-load branch in Chart's layout effect fall
      // through to `fitToContent`, which shows the last 250 bars of
      // the new TF — what the user expects when they switch.
      t.viewport = null;
      t.priceRange = null;
    },
    setSymbol(state, action: PayloadAction<WithTab<{ symbol: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.symbol = action.payload.symbol;
      // Same reasoning as setTimeframe — a viewport / priceRange
      // saved for one instrument rarely intersects another's data.
      t.viewport = null;
      t.priceRange = null;
    },
    setAnalysisStart(state, action: PayloadAction<WithTab<{ value: string }>>) {
      getTab(state, action.payload.tabId).analysisStart = action.payload.value;
    },
    setAnalysisEnd(state, action: PayloadAction<WithTab<{ value: string }>>) {
      getTab(state, action.payload.tabId).analysisEnd = action.payload.value;
    },
    setChartType(state, action: PayloadAction<WithTab<{ chartType: ChartType }>>) {
      getTab(state, action.payload.tabId).chartType = action.payload.chartType;
    },
    setDrawingTool(state, action: PayloadAction<WithTab<{ tool: DrawingTool }>>) {
      const t = getTab(state, action.payload.tabId);
      t.activeDrawingTool = action.payload.tool;
      // Switching tools cancels any in-progress two-click placement
      // (fib, position, or diagonal) and any path draft.
      t.fibAnchor = null;
      t.positionAnchor = null;
      t.diagonalAnchor = null;
      t.priceRangeAnchor = null;
      t.textAnchor = null;
      t.pathDraft = null;
    },
    setFibAnchor(
      state,
      action: PayloadAction<WithTab<{ anchor: { time: number; price: number } | null }>>,
    ) {
      getTab(state, action.payload.tabId).fibAnchor = action.payload.anchor;
    },
    setPositionAnchor(
      state,
      action: PayloadAction<WithTab<{ anchor: { time: number; price: number } | null }>>,
    ) {
      getTab(state, action.payload.tabId).positionAnchor = action.payload.anchor;
    },
    addFib(state, action: PayloadAction<WithTab<{ fib: FibRetracement }>>) {
      const t = getTab(state, action.payload.tabId);
      t.fibRetracements.push(action.payload.fib);
      t.fibAnchor = null;
      t.activeDrawingTool = null;
    },
    updateFib(
      state,
      action: PayloadAction<
        WithTab<{ id: string } & Partial<Omit<FibRetracement, "id">>>
      >,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const fib = t.fibRetracements.find((f) => f.id === id);
      if (fib) Object.assign(fib, rest);
    },
    removeFib(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.fibRetracements = t.fibRetracements.filter((f) => f.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "fib" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    addHorizontalLine(state, action: PayloadAction<WithTab<{ line: HorizontalLine }>>) {
      const t = getTab(state, action.payload.tabId);
      t.horizontalLines.push(action.payload.line);
      t.activeDrawingTool = null;
    },
    updateHorizontalLine(
      state,
      action: PayloadAction<WithTab<{ id: string } & Partial<Omit<HorizontalLine, "id">>>>,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const line = t.horizontalLines.find((l) => l.id === id);
      if (line) Object.assign(line, rest);
    },
    removeHorizontalLine(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.horizontalLines = t.horizontalLines.filter((l) => l.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "hline" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    addVerticalLine(state, action: PayloadAction<WithTab<{ line: VerticalLine }>>) {
      const t = getTab(state, action.payload.tabId);
      t.verticalLines.push(action.payload.line);
      t.activeDrawingTool = null;
    },
    updateVerticalLine(
      state,
      action: PayloadAction<WithTab<{ id: string } & Partial<Omit<VerticalLine, "id">>>>,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const line = t.verticalLines.find((l) => l.id === id);
      if (line) Object.assign(line, rest);
    },
    removeVerticalLine(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.verticalLines = t.verticalLines.filter((l) => l.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "vline" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    setDiagonalAnchor(
      state,
      action: PayloadAction<WithTab<{ anchor: { time: number; price: number } | null }>>,
    ) {
      getTab(state, action.payload.tabId).diagonalAnchor = action.payload.anchor;
    },
    addDiagonalLine(state, action: PayloadAction<WithTab<{ line: DiagonalLine }>>) {
      const t = getTab(state, action.payload.tabId);
      t.diagonalLines.push(action.payload.line);
      t.diagonalAnchor = null;
      t.activeDrawingTool = null;
    },
    updateDiagonalLine(
      state,
      action: PayloadAction<
        WithTab<{ id: string } & Partial<Omit<DiagonalLine, "id">>>
      >,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const line = t.diagonalLines.find((l) => l.id === id);
      if (line) Object.assign(line, rest);
    },
    removeDiagonalLine(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.diagonalLines = t.diagonalLines.filter((l) => l.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "diagonal" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    /** Add a vertex to the in-progress path. Creates the draft on
     *  the first call. */
    appendPathVertex(state, action: PayloadAction<WithTab<{ point: PathPoint }>>) {
      const t = getTab(state, action.payload.tabId);
      if (t.pathDraft === null) {
        t.pathDraft = [action.payload.point];
      } else {
        t.pathDraft.push(action.payload.point);
      }
    },
    /** Drop the in-progress path draft (Esc / tool switch / first
     *  click was a misclick). */
    cancelPathDraft(state, action: PayloadAction<{ tabId: string }>) {
      getTab(state, action.payload.tabId).pathDraft = null;
    },
    /** Finish the in-progress path. If the draft has < 2 vertices the
     *  draft is silently discarded — a single-point path makes no
     *  sense.
     *
     *  `dropLast` is set by the dblclick path-commit because the
     *  browser fires *click → click → dblclick*: both clicks of a
     *  double-click already appended vertices to the draft, so the
     *  final vertex is a near-duplicate of its predecessor. Drop it
     *  before persisting. Esc-commit doesn't set this since no extra
     *  click is involved. */
    commitPathDraft(
      state,
      action: PayloadAction<WithTab<{ id: string; dropLast?: boolean }>>,
    ) {
      const t = getTab(state, action.payload.tabId);
      if (t.pathDraft) {
        const trimmed =
          action.payload.dropLast && t.pathDraft.length >= 1
            ? t.pathDraft.slice(0, -1)
            : t.pathDraft;
        if (trimmed.length >= 2) {
          t.paths.push({ id: action.payload.id, points: trimmed });
          t.activeDrawingTool = null;
        }
      }
      t.pathDraft = null;
    },
    /** Update top-level path metadata (visible, locked, color, width, style). */
    updatePath(
      state,
      action: PayloadAction<WithTab<{ id: string } & Partial<Omit<Path, "id" | "points">>>>,
    ) {
      const { tabId, id, ...patch } = action.payload;
      const t = getTab(state, tabId);
      const p = t.paths.find((x) => x.id === id);
      if (!p) return;
      Object.assign(p, patch);
    },
    /** Drag a vertex of a committed path. */
    updatePathVertex(
      state,
      action: PayloadAction<WithTab<{ id: string; index: number } & PathPoint>>,
    ) {
      const { tabId, id, index, time, price } = action.payload;
      const t = getTab(state, tabId);
      const p = t.paths.find((x) => x.id === id);
      if (!p || index < 0 || index >= p.points.length) return;
      p.points[index] = { time, price };
    },
    /** Insert a new vertex at the given index (between two existing
     *  vertices). Triggered by clicking on an existing segment. */
    insertPathVertex(
      state,
      action: PayloadAction<WithTab<{ id: string; index: number } & PathPoint>>,
    ) {
      const { tabId, id, index, time, price } = action.payload;
      const t = getTab(state, tabId);
      const p = t.paths.find((x) => x.id === id);
      if (!p) return;
      const clamped = Math.max(0, Math.min(index, p.points.length));
      p.points.splice(clamped, 0, { time, price });
    },
    /** Remove a single vertex from a path. If that would leave the
     *  path with < 2 vertices the whole path is removed instead. */
    removePathVertex(
      state,
      action: PayloadAction<WithTab<{ id: string; index: number }>>,
    ) {
      const { tabId, id, index } = action.payload;
      const t = getTab(state, tabId);
      const p = t.paths.find((x) => x.id === id);
      if (!p) return;
      if (p.points.length <= 2) {
        t.paths = t.paths.filter((x) => x.id !== id);
        if (t.selectedDrawing?.kind === "path" && t.selectedDrawing.id === id) {
          t.selectedDrawing = null;
        }
        return;
      }
      p.points.splice(index, 1);
    },
    removePath(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.paths = t.paths.filter((p) => p.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "path" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    addPosition(state, action: PayloadAction<WithTab<{ position: Position }>>) {
      const t = getTab(state, action.payload.tabId);
      t.positions.push(action.payload.position);
      // Mirror the fib commit flow: clear the anchor and deactivate
      // the tool so the next click won't accidentally start another
      // placement.
      t.positionAnchor = null;
      t.activeDrawingTool = null;
    },
    updatePosition(
      state,
      action: PayloadAction<
        WithTab<{ id: string } & Partial<Omit<Position, "id">>>
      >,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const pos = t.positions.find((p) => p.id === id);
      if (pos) Object.assign(pos, rest);
    },
    removePosition(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.positions = t.positions.filter((p) => p.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "position" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
      if (state.positionDialogId === action.payload.id) {
        state.positionDialogId = null;
      }
    },
    setPriceRangeAnchor(
      state,
      action: PayloadAction<WithTab<{ anchor: { time: number; price: number } | null }>>,
    ) {
      getTab(state, action.payload.tabId).priceRangeAnchor = action.payload.anchor;
    },
    addPriceRangeBox(state, action: PayloadAction<WithTab<{ box: PriceRangeBox }>>) {
      const t = getTab(state, action.payload.tabId);
      t.priceRangeBoxes.push(action.payload.box);
      t.priceRangeAnchor = null;
      t.activeDrawingTool = null;
    },
    updatePriceRangeBox(
      state,
      action: PayloadAction<
        WithTab<{ id: string } & Partial<Omit<PriceRangeBox, "id">>>
      >,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const box = t.priceRangeBoxes.find((b) => b.id === id);
      if (box) Object.assign(box, rest);
    },
    removePriceRangeBox(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.priceRangeBoxes = t.priceRangeBoxes.filter(
        (b) => b.id !== action.payload.id,
      );
      if (
        t.selectedDrawing?.kind === "priceRange" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    addTextLabel(state, action: PayloadAction<WithTab<{ label: TextLabel }>>) {
      const t = getTab(state, action.payload.tabId);
      t.textLabels.push(action.payload.label);
      t.textAnchor = null;
      t.activeDrawingTool = null;
    },
    updateTextLabel(
      state,
      action: PayloadAction<WithTab<{ id: string } & Partial<Omit<TextLabel, "id">>>>,
    ) {
      const { tabId, id, ...rest } = action.payload;
      const t = getTab(state, tabId);
      const lbl = t.textLabels.find((l) => l.id === id);
      if (lbl) Object.assign(lbl, rest);
    },
    removeTextLabel(state, action: PayloadAction<WithTab<{ id: string }>>) {
      const t = getTab(state, action.payload.tabId);
      t.textLabels = t.textLabels.filter((l) => l.id !== action.payload.id);
      if (
        t.selectedDrawing?.kind === "text" &&
        t.selectedDrawing.id === action.payload.id
      ) {
        t.selectedDrawing = null;
      }
    },
    setTextAnchor(
      state,
      action: PayloadAction<WithTab<{ anchor: { time: number; price: number } | null }>>,
    ) {
      getTab(state, action.payload.tabId).textAnchor = action.payload.anchor;
    },
    setObjectsTreeOpen(state, action: PayloadAction<boolean>) {
      state.objectsTreeOpen = action.payload;
    },
    toggleObjectsTree(state) {
      state.objectsTreeOpen = !state.objectsTreeOpen;
    },
    setPositionDialogId(state, action: PayloadAction<string | null>) {
      state.positionDialogId = action.payload;
    },
    setSelectedDrawing(
      state,
      action: PayloadAction<WithTab<{ selected: SelectedDrawing | null }>>,
    ) {
      getTab(state, action.payload.tabId).selectedDrawing = action.payload.selected;
    },
    setFibLevelsDialogOpen(state, action: PayloadAction<boolean>) {
      state.fibLevelsDialogOpen = action.payload;
    },
    setOneClickPanelOpen(state, action: PayloadAction<boolean>) {
      state.oneClickPanelOpen = action.payload;
    },
    toggleOneClickPanel(state) {
      state.oneClickPanelOpen = !state.oneClickPanelOpen;
    },
    setTradingPanelOpen(state, action: PayloadAction<boolean>) {
      state.tradingPanelOpen = action.payload;
    },
    setTradeVolume(state, action: PayloadAction<number>) {
      // Clamp to a sane min lot; round to 2dp to avoid float dust.
      const v = Math.max(0.01, Math.round(action.payload * 100) / 100);
      state.tradeVolume = v;
    },
    setJournalOpen(state, action: PayloadAction<boolean>) {
      state.journalOpen = action.payload;
      // Notes + Journal + Help + News are docked drawers anchored to the
      // same slot, so they fully overlap. Keep them mutually exclusive —
      // opening the Journal closes the others so the newly-opened drawer
      // always takes focus (no z-index race on open order).
      if (action.payload) {
        state.notesOpen = false;
        state.helpOpen = false;
        state.newsOpen = false;
        state.chatOpen = false;
      }
    },
    setSignalChartOpen(state, action: PayloadAction<boolean>) {
      // Only act on the closed→open edge: re-clicking the already-active
      // Signal pill must not re-open a drawer the user just closed.
      const opening = action.payload && !state.signalChartOpen;
      state.signalChartOpen = action.payload;
      if (opening) {
        // Chart + orders are one feature — selecting the Signal tab shows
        // both, so the running positions are visible without hunting for
        // the rail button.
        state.signalOrdersOpen = true;
      } else if (!action.payload) {
        // Leaving the Signal view collapses its drawer with it. The drawer
        // lists the SIGNAL account's orders, so left open over a normal chart
        // it reads as that chart's orders — and it steals width from the very
        // chart the user switched to.
        state.signalOrdersOpen = false;
      }
    },
    setSignalOrdersOpen(state, action: PayloadAction<boolean>) {
      state.signalOrdersOpen = action.payload;
    },
    setPlainDarkChart(state, action: PayloadAction<boolean>) {
      state.prefs.plainDarkChart = action.payload;
    },
    setMonoCandles(state, action: PayloadAction<boolean>) {
      state.prefs.monoCandles = action.payload;
    },
    /** Patch arbitrary fields of the chart theme (used by the
     *  individual color pickers in the dialog). */
    patchChartTheme(state, action: PayloadAction<Partial<ChartTheme>>) {
      state.prefs.theme = { ...state.prefs.theme, ...action.payload };
    },
    /** Replace the whole active theme — used by the dialog when
     *  the user switches preset (whole-theme swap instead of N
     *  field patches in a row). */
    setChartTheme(state, action: PayloadAction<ChartTheme>) {
      state.prefs.theme = action.payload;
    },
    /** Cache per-scheme customisations. Dialog calls this on Save
     *  for each scheme the user touched so the next "switch back
     *  to scheme X" can replay the user's edits instead of
     *  resetting to defaults. */
    setChartThemeCache(
      state,
      action: PayloadAction<Partial<Record<ChartScheme, ChartTheme>>>,
    ) {
      state.prefs.themeCache = {
        ...state.prefs.themeCache,
        ...action.payload,
      };
    },
    /** Set or clear the per-tab theme override for one app UI mode.
     *  `theme: null` clears it → the tab falls back to User scope (or
     *  built-in defaults if no user-scope theme is saved). `mode`
     *  selects the dark or light slot so the two modes customise
     *  independently. */
    setTabTheme(
      state,
      action: PayloadAction<WithTab<{ mode: AppTheme; theme: ChartTheme | null }>>,
    ) {
      const tab = getTab(state, action.payload.tabId);
      if (action.payload.mode === "light") tab.themeLight = action.payload.theme;
      else tab.theme = action.payload.theme;
    },
    /** Replace the per-tab per-scheme cache for one app UI mode. Lets
     *  the Properties dialog at Workspace scope flip between Red/Green
     *  and Mono without losing per-scheme tweaks on THIS tab + mode. */
    setTabThemeCache(
      state,
      action: PayloadAction<
        WithTab<{
          mode: AppTheme;
          themeCache: Partial<Record<ChartScheme, ChartTheme>>;
        }>
      >,
    ) {
      const tab = getTab(state, action.payload.tabId);
      if (action.payload.mode === "light")
        tab.themeCacheLight = action.payload.themeCache;
      else tab.themeCache = action.payload.themeCache;
    },
    /** Set or clear the per-tab (chart-scope) Fibonacci levels
     *  override. Same null = inherit semantics as setTabTheme. */
    setTabFibLevels(
      state,
      action: PayloadAction<WithTab<{ fibLevels: FibLevel[] | null }>>,
    ) {
      const tab = getTab(state, action.payload.tabId);
      tab.fibLevels = action.payload.fibLevels;
    },
    /** Set or clear the workspace-scope Fibonacci levels
     *  override. `null` = inherit from User scope (the global
     *  fibLevelsSlice.levels loaded from /api/fib_levels). */
    setWorkspaceFibLevels(
      state,
      action: PayloadAction<FibLevel[] | null>,
    ) {
      state.prefs.fibLevels = action.payload;
    },
    /** Per-tab fib stroke width override. */
    setTabFibLineWidth(
      state,
      action: PayloadAction<WithTab<{ lineWidth: number | null }>>,
    ) {
      const tab = getTab(state, action.payload.tabId);
      tab.fibLineWidth = action.payload.lineWidth;
    },
    /** Per-tab fib stroke style override. */
    setTabFibLineStyle(
      state,
      action: PayloadAction<WithTab<{ lineStyle: LineStyleName | null }>>,
    ) {
      const tab = getTab(state, action.payload.tabId);
      tab.fibLineStyle = action.payload.lineStyle;
    },
    setIndicators(
      state,
      action: PayloadAction<WithTab<{ indicators: IndicatorSettings | null }>>,
    ) {
      const tab = getTab(state, action.payload.tabId);
      tab.indicators = action.payload.indicators;
    },
    /** Set or clear the per-tab Weekly Behaviour override.
     *  `null` clears → the tab falls back to User scope. */
    setTabWeeklyBehaviour(
      state,
      action: PayloadAction<
        WithTab<{ weeklyBehaviour: WeeklyBehaviourSettings | null }>
      >,
    ) {
      const tab = getTab(state, action.payload.tabId);
      tab.weeklyBehaviour = action.payload.weeklyBehaviour;
    },
    setWeeklyBehaviourDialogOpen(state, action: PayloadAction<boolean>) {
      state.weeklyBehaviourDialogOpen = action.payload;
    },
    setNotesOpen(state, action: PayloadAction<boolean>) {
      state.notesOpen = action.payload;
      // Mutually exclusive with the other docked drawers — see setJournalOpen.
      if (action.payload) {
        state.journalOpen = false;
        state.helpOpen = false;
        state.newsOpen = false;
        state.chatOpen = false;
      }
    },
    setImageBoardOpen(state, action: PayloadAction<boolean>) {
      state.imageBoardOpen = action.payload;
    },
    /** Ask the Notes drawer to open, expand a note and scroll to it.
     *  Closes the board, since the point of a jump is to land on the
     *  note. The drawer clears the request once it has acted. */
    requestNoteFocus(state, action: PayloadAction<string>) {
      state.focusNoteId = action.payload;
      state.imageBoardOpen = false;
      state.notesOpen = true;
      state.journalOpen = false;
      state.helpOpen = false;
      state.newsOpen = false;
    },
    clearNoteFocus(state) {
      state.focusNoteId = null;
    },
    /** Journal twin of requestNoteFocus. */
    requestJournalFocus(state, action: PayloadAction<string>) {
      state.focusJournalKey = action.payload;
      state.imageBoardOpen = false;
      state.journalOpen = true;
      state.notesOpen = false;
      state.helpOpen = false;
      state.newsOpen = false;
    },
    clearJournalFocus(state) {
      state.focusJournalKey = null;
    },
    setHelpOpen(state, action: PayloadAction<boolean>) {
      state.helpOpen = action.payload;
      // Mutually exclusive with the other docked drawers — see setJournalOpen.
      if (action.payload) {
        state.journalOpen = false;
        state.notesOpen = false;
        state.newsOpen = false;
        state.chatOpen = false;
      }
    },
    setNewsOpen(state, action: PayloadAction<boolean>) {
      state.newsOpen = action.payload;
      // Mutually exclusive with the other docked drawers — see setJournalOpen.
      if (action.payload) {
        state.journalOpen = false;
        state.notesOpen = false;
        state.helpOpen = false;
        state.chatOpen = false;
      }
    },
    setChatOpen(state, action: PayloadAction<boolean>) {
      state.chatOpen = action.payload;
      // Mutually exclusive with the other docked drawers — see setJournalOpen.
      if (action.payload) {
        state.journalOpen = false;
        state.notesOpen = false;
        state.helpOpen = false;
        state.newsOpen = false;
      }
    },
    setChatTab(state, action: PayloadAction<ChatPanelTab>) {
      state.chatTab = action.payload;
    },
    setTerminalOpen(state, action: PayloadAction<boolean>) {
      state.terminalOpen = action.payload;
    },
    /** Replace the whole theme with one of the two built-in
     *  presets — used by the "Use Red/Green" / "Use Mono Black"
     *  buttons in the dialog. Also flips the legacy quick-toggles
     *  so right-click-menu state stays consistent. */
    applyChartScheme(state, action: PayloadAction<ChartScheme>) {
      const scheme = action.payload;
      state.prefs.theme = { ...CHART_SCHEMES[scheme] };
      state.prefs.monoCandles = scheme === "monoBlack";
      state.prefs.plainDarkChart = scheme === "monoBlack";
    },
    setChartColorsDialogOpen(state, action: PayloadAction<boolean>) {
      state.chartColorsDialogOpen = action.payload;
    },
    /** Open (toolId) or close (null) the shared tool-settings panel. */
    setSettingsToolId(state, action: PayloadAction<string | null>) {
      state.settingsToolId = action.payload;
    },
    /** Persist the current chart viewport for the active tab. Chart
     *  calls this debounced from its range-change subscription so we
     *  don't churn redux on every wheel tick. */
    setViewport(
      state,
      action: PayloadAction<WithTab<{ viewport: { from: number; to: number } }>>,
    ) {
      getTab(state, action.payload.tabId).viewport = action.payload.viewport;
    },
    setShowTradingTrades(
      state,
      action: PayloadAction<WithTab<{ show: boolean }>>,
    ) {
      getTab(state, action.payload.tabId).showTradingTrades = action.payload.show;
    },
    setShowTradingSlTp(
      state,
      action: PayloadAction<WithTab<{ show: boolean }>>,
    ) {
      getTab(state, action.payload.tabId).showTradingSlTp = action.payload.show;
    },
    setShowHistoryOrders(
      state,
      action: PayloadAction<WithTab<{ show: boolean }>>,
    ) {
      getTab(state, action.payload.tabId).showHistoryOrders =
        action.payload.show;
    },
    /** Persist the right price scale's margins (top/bottom) for the
     *  tab. Dispatched from Chart on tab switch / page hide so the
     *  per-tab scale survives across tab switches and reloads. */
    setScaleMargins(
      state,
      action: PayloadAction<
        WithTab<{ scaleMargins: { top: number; bottom: number } }>
      >,
    ) {
      getTab(state, action.payload.tabId).scaleMargins =
        action.payload.scaleMargins;
    },
    /** Persist the tab's visible price range from the user's right-
     *  axis drag. Pass `priceRange: null` to clear (means "next time
     *  this tab activates, let autoScale fit to data instead of
     *  restoring a locked range"). */
    setPriceRange(
      state,
      action: PayloadAction<
        WithTab<{ priceRange: { from: number; to: number } | null }>
      >,
    ) {
      getTab(state, action.payload.tabId).priceRange =
        action.payload.priceRange;
    },
    clearAllDrawings(state, action: PayloadAction<{ tabId: string }>) {
      const t = getTab(state, action.payload.tabId);
      t.fibRetracements = [];
      t.fibAnchor = null;
      t.horizontalLines = [];
      t.verticalLines = [];
      t.diagonalLines = [];
      t.diagonalAnchor = null;
      t.paths = [];
      t.pathDraft = null;
      t.positions = [];
      t.positionAnchor = null;
      t.priceRangeBoxes = [];
      t.priceRangeAnchor = null;
      t.textLabels = [];
      t.textAnchor = null;
      t.activeDrawingTool = null;
      t.selectedDrawing = null;
    },
    /** Replace all drawings on screen with the contents of a saved
     *  set. Used by the Load button in the Drawings dialog. */
    loadDrawings(
      state,
      action: PayloadAction<
        WithTab<{
          fibs: FibRetracement[];
          horizontal_lines: HorizontalLine[];
          vertical_lines: VerticalLine[];
          diagonal_lines?: DiagonalLine[];
          paths?: Path[];
          positions?: Position[];
          price_ranges?: PriceRangeBox[];
          text_labels?: TextLabel[];
        }>
      >,
    ) {
      const t = getTab(state, action.payload.tabId);
      t.fibRetracements = action.payload.fibs;
      t.horizontalLines = action.payload.horizontal_lines;
      t.verticalLines = action.payload.vertical_lines;
      t.diagonalLines = action.payload.diagonal_lines ?? [];
      t.paths = action.payload.paths ?? [];
      t.positions = action.payload.positions ?? [];
      t.priceRangeBoxes = action.payload.price_ranges ?? [];
      t.textLabels = action.payload.text_labels ?? [];
      t.fibAnchor = null;
      t.diagonalAnchor = null;
      t.pathDraft = null;
      t.positionAnchor = null;
      t.priceRangeAnchor = null;
      t.textAnchor = null;
      t.activeDrawingTool = null;
      t.selectedDrawing = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTab, (state, action) => {
        // Seed per-tab UI state when a new tab is created. Without
        // this the tab would have undefined ui state until the first
        // dispatch hits getTab().
        if (!state.byTab[action.payload.id]) {
          state.byTab[action.payload.id] = createDefaultUiTabState();
        }
      })
      .addCase(removeTab, (state, action) => {
        delete state.byTab[action.payload];
      })
      .addCase(hydrateWorkspace, (state, action) => {
        // Replace byTab with the workspace payload. Each tab's ui
        // state is rebuilt from a fresh default, then overlaid with
        // the persisted fields (so anything we add to UiTabState
        // later that isn't in old saves still gets sane defaults).
        const next: Record<string, UiTabState> = {};
        for (const [tabId, saved] of Object.entries(action.payload.byTab)) {
          const base = createDefaultUiTabState();
          base.symbol = saved.symbol;
          base.timeframe = saved.timeframe;
          base.analysisStart = saved.analysisStart;
          base.analysisEnd = saved.analysisEnd;
          base.chartType = saved.chartType;
          base.fibRetracements = saved.drawings?.fibs ?? [];
          base.horizontalLines = saved.drawings?.horizontal_lines ?? [];
          base.verticalLines = saved.drawings?.vertical_lines ?? [];
          base.diagonalLines = saved.drawings?.diagonal_lines ?? [];
          base.paths = saved.drawings?.paths ?? [];
          base.positions = saved.drawings?.positions ?? [];
          base.priceRangeBoxes = saved.drawings?.price_ranges ?? [];
          base.textLabels = saved.drawings?.text_labels ?? [];
          base.viewport = saved.viewport ?? null;
          base.scaleMargins = saved.scaleMargins ?? null;
          // priceRange is deliberately NOT restored. See issue #86,
          // "Price-axis drag survives tab switch + workspace reload" — our
          // own lock effect made the page-hide capture indistinguishable
          // from a real user drag, so saved priceRanges in older
          // workspaces are likely garbage. Ignoring them on hydrate
          // means a single reload after deploying the fix wipes the
          // bad value: render with auto-fit → next page-hide writes
          // priceRange:null back to the workspace.
          base.priceRange = null;
          // the trading server overlay visibility — three independent
          // layers. Entry lines + SL/TP default ON, history markers OFF
          // (see createDefaultUiTabState). Honour explicit booleans from
          // older saves so a deliberate choice survives.
          //
          // Legacy fields (`showTradingPositions` / `showTradingOrders`)
          // are read only if explicitly set on the saved tab.
          if (typeof saved.showTradingTrades === "boolean") {
            base.showTradingTrades = saved.showTradingTrades;
          } else {
            const legacy = saved as {
              showTradingPositions?: boolean;
              showTradingOrders?: boolean;
            };
            if (
              typeof legacy.showTradingPositions === "boolean" ||
              typeof legacy.showTradingOrders === "boolean"
            ) {
              base.showTradingTrades =
                (legacy.showTradingPositions ?? false) ||
                (legacy.showTradingOrders ?? false);
            } else {
              // No stored preference at all — take the new default ON.
              base.showTradingTrades = true;
            }
          }
          // SL/TP: honour an explicit saved boolean (someone who turned it
          // off keeps it off), else fall back to the new default ON.
          if (typeof saved.showTradingSlTp === "boolean") {
            base.showTradingSlTp = saved.showTradingSlTp;
          } else {
            const legacy = saved as { showTradingPositions?: boolean };
            base.showTradingSlTp =
              typeof legacy.showTradingPositions === "boolean"
                ? legacy.showTradingPositions
                : true;
          }
          base.showHistoryOrders =
            typeof saved.showHistoryOrders === "boolean"
              ? saved.showHistoryOrders
              : false;
          // Per-tab theme + fibLevels — round-trip directly.
          // Each scope either OWNS its config or inherits; no
          // partial merging across scopes.
          const savedTabExt = saved as {
            theme?: ChartTheme | null;
            themeCache?: Partial<Record<ChartScheme, ChartTheme>>;
            themeLight?: ChartTheme | null;
            themeCacheLight?: Partial<Record<ChartScheme, ChartTheme>>;
            fibLevels?: FibLevel[] | null;
            fibLineWidth?: number | null;
            fibLineStyle?: LineStyleName | null;
            weeklyBehaviour?: WeeklyBehaviourSettings | null;
            indicators?: IndicatorSettings | null;
          };
          base.theme = savedTabExt.theme ?? null;
          base.themeCache = savedTabExt.themeCache ?? undefined;
          base.themeLight = savedTabExt.themeLight ?? null;
          base.themeCacheLight = savedTabExt.themeCacheLight ?? undefined;
          base.fibLevels = savedTabExt.fibLevels ?? null;
          base.fibLineWidth = savedTabExt.fibLineWidth ?? null;
          base.fibLineStyle = savedTabExt.fibLineStyle ?? null;
          base.weeklyBehaviour = savedTabExt.weeklyBehaviour ?? null;
          base.indicators = savedTabExt.indicators ?? null;
          next[tabId] = base;
        }
        state.byTab = next;

        // Legacy migration: pre-2-scope workspaces stored ONE
        // theme + themeCache at the workspace level
        // (prefs.theme / prefs.themeCache). The simplified model
        // dropped that scope — every chart now owns its theme.
        // Copy the legacy workspace-wide theme to each tab that
        // doesn't have its own, so existing users see no visual
        // change after the upgrade. The legacy fields aren't
        // re-written; once each tab carries its theme, the
        // autosave will stop including them.
        const legacyWorkspaceTheme = (
          action.payload.prefs as { theme?: ChartTheme } | undefined
        )?.theme;
        const legacyWorkspaceCache = (
          action.payload.prefs as {
            themeCache?: Partial<Record<ChartScheme, ChartTheme>>;
          } | undefined
        )?.themeCache;
        if (legacyWorkspaceTheme || legacyWorkspaceCache) {
          for (const tabId of Object.keys(state.byTab)) {
            const tab = state.byTab[tabId];
            if (!tab.theme && legacyWorkspaceTheme) {
              tab.theme = { ...legacyWorkspaceTheme };
            }
            if (!tab.themeCache && legacyWorkspaceCache) {
              tab.themeCache = { ...legacyWorkspaceCache };
            }
          }
        }

        // Same migration story for legacy workspace-wide fib
        // levels (prefs.fibLevels): seed each tab's fibLevels
        // from the old value if the tab didn't carry its own.
        const legacyWorkspaceFib = (
          action.payload.prefs as { fibLevels?: FibLevel[] | null }
            | undefined
        )?.fibLevels;
        if (legacyWorkspaceFib) {
          for (const tabId of Object.keys(state.byTab)) {
            const tab = state.byTab[tabId];
            if (!tab.fibLevels) {
              tab.fibLevels = legacyWorkspaceFib.map((l) => ({ ...l }));
            }
          }
        }
        // Modals stay closed across hydration — they were transient
        // anyway and reopening them mid-load would surprise the user.
        state.fibLevelsDialogOpen = false;
        state.positionDialogId = null;
        state.chartColorsDialogOpen = false;
        state.weeklyBehaviourDialogOpen = false;
        state.notesOpen = false;
        state.helpOpen = false;
        state.newsOpen = false;
        state.chatOpen = false;
        state.imageBoardOpen = false;
        state.focusNoteId = null;
        state.focusJournalKey = null;
        // Workspace-wide prefs. Old workspaces won't have the
        // `theme` field; derive it from the legacy plainDarkChart/
        // monoCandles toggles so the chart still renders with the
        // colors the user picked before the theme system existed.
        const savedPrefs = action.payload.prefs as
          | {
              plainDarkChart?: boolean;
              monoCandles?: boolean;
              theme?: Partial<ChartTheme>;
              themeCache?: Partial<Record<ChartScheme, Partial<ChartTheme>>>;
              fibLevels?: FibLevel[] | null;
            }
          | undefined;
        const plainDark =
          typeof savedPrefs?.plainDarkChart === "boolean"
            ? savedPrefs.plainDarkChart
            : false;
        const mono =
          typeof savedPrefs?.monoCandles === "boolean"
            ? savedPrefs.monoCandles
            : false;
        // Theme resolution: saved.theme wins if present; otherwise
        // pick the matching preset based on the legacy toggles.
        // Partial saves are merged on top of the preset so a
        // future schema addition doesn't break existing saves.
        const baseScheme: ChartScheme = mono ? "monoBlack" : "redGreen";
        const theme: ChartTheme = savedPrefs?.theme
          ? { ...CHART_SCHEMES[baseScheme], ...savedPrefs.theme }
          : { ...CHART_SCHEMES[baseScheme] };
        // Per-scheme customisation cache. Each saved entry is
        // merged on top of its scheme defaults so a stored partial
        // (older schema) still round-trips cleanly. Also seed the
        // active scheme's slot from the active theme so the very
        // first "switch back to this scheme" replays the user's
        // current look, not the bare preset defaults.
        const themeCache: Partial<Record<ChartScheme, ChartTheme>> = {};
        const savedCache = savedPrefs?.themeCache;
        for (const scheme of Object.keys(CHART_SCHEMES) as ChartScheme[]) {
          const cached = savedCache?.[scheme];
          if (cached) {
            themeCache[scheme] = {
              ...CHART_SCHEMES[scheme],
              ...cached,
            };
          }
        }
        if (!themeCache[baseScheme]) {
          themeCache[baseScheme] = theme;
        }
        state.prefs = {
          plainDarkChart: plainDark,
          monoCandles: mono,
          theme,
          themeCache,
          // Workspace-scope fib levels — `null`/absent in the saved
          // workspace means "inherit from User" (the fibLevelsSlice
          // value loaded from /api/fib_levels). Stored as a full
          // list when overridden, same as the theme above.
          fibLevels: Array.isArray(savedPrefs?.fibLevels)
            ? savedPrefs!.fibLevels
            : null,
        };
      });
  },
});

export const {
  setTimeframe,
  setSymbol,
  setAnalysisStart,
  setAnalysisEnd,
  setChartType,
  setDrawingTool,
  setFibAnchor,
  addFib,
  updateFib,
  removeFib,
  addHorizontalLine,
  updateHorizontalLine,
  removeHorizontalLine,
  addVerticalLine,
  updateVerticalLine,
  removeVerticalLine,
  setDiagonalAnchor,
  addDiagonalLine,
  updateDiagonalLine,
  removeDiagonalLine,
  appendPathVertex,
  cancelPathDraft,
  commitPathDraft,
  updatePath,
  updatePathVertex,
  insertPathVertex,
  removePathVertex,
  removePath,
  addPosition,
  updatePosition,
  removePosition,
  setPositionAnchor,
  setPositionDialogId,
  addPriceRangeBox,
  updatePriceRangeBox,
  removePriceRangeBox,
  setPriceRangeAnchor,
  setSelectedDrawing,
  setFibLevelsDialogOpen,
  setJournalOpen,
  setOneClickPanelOpen,
  toggleOneClickPanel,
  setTradingPanelOpen,
  setTradeVolume,
  setMonoCandles,
  setPlainDarkChart,
  patchChartTheme,
  setChartTheme,
  setChartThemeCache,
  setTabTheme,
  setTabThemeCache,
  setTabFibLevels,
  setTabFibLineWidth,
  setTabFibLineStyle,
  setTabWeeklyBehaviour,
  setWeeklyBehaviourDialogOpen,
  setNotesOpen,
  setImageBoardOpen,
  requestNoteFocus,
  clearNoteFocus,
  requestJournalFocus,
  clearJournalFocus,
  setSignalChartOpen,
  setSignalOrdersOpen,
  setHelpOpen,
  setNewsOpen,
  setChatOpen,
  setChatTab,
  setTerminalOpen,
  setWorkspaceFibLevels,
  applyChartScheme,
  setChartColorsDialogOpen,
  setSettingsToolId,
  setViewport,
  setShowTradingTrades,
  setShowTradingSlTp,
  setShowHistoryOrders,
  setScaleMargins,
  setPriceRange,
  clearAllDrawings,
  loadDrawings,
  setIndicators,
  addTextLabel,
  updateTextLabel,
  removeTextLabel,
  setTextAnchor,
  setObjectsTreeOpen,
  toggleObjectsTree,
} = uiSlice.actions;
export default uiSlice.reducer;
